"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
} from "react";
import { formatNumber } from "@/lib/formatting/numbers";
import {
  isCheckoutQuantityAllowed,
  stepQuantityDown,
  stepQuantityUp,
} from "@shared/products/checkout";
import { useDepsChanged } from "@/hooks/use-synced-state";
import { useHoldRepeat } from "./use-hold-repeat";

interface QuantityStepperInput {
  quantity: number;
  minQuantity: number;
  step?: number;
  onUpdateQuantity: (quantity: number) => void | Promise<void>;
  onPendingChange?: (pending: boolean) => void;
}

function useQuantityCommit({
  quantity,
  onUpdateQuantity,
  onPendingChange,
}: QuantityStepperInput) {
  const [localQty, setLocalQty] = useState(formatNumber(quantity));
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const onUpdateQuantityRef = useRef(onUpdateQuantity);
  const onPendingChangeRef = useRef(onPendingChange);
  const quantityRef = useRef(quantity);
  useEffect(() => {
    onUpdateQuantityRef.current = onUpdateQuantity;
    onPendingChangeRef.current = onPendingChange;
    quantityRef.current = quantity;
  });

  if (useDepsChanged([quantity])) {
    setLocalQty(formatNumber(quantity));
  }

  const cancelPending = useCallback(() => {
    clearTimeout(debounceRef.current);
    onPendingChangeRef.current?.(false);
  }, []);

  const commit = useCallback(
    async (value: number) => {
      cancelPending();
      setLocalQty(formatNumber(value));
      try {
        await onUpdateQuantityRef.current(value);
      } catch {
        setLocalQty(formatNumber(quantityRef.current));
      }
    },
    [cancelPending],
  );

  useEffect(() => cancelPending, [cancelPending]);

  return { localQty, setLocalQty, debounceRef, cancelPending, commit };
}

function useHeldSteps(input: {
  base: number;
  minQuantity: number;
  step: number;
  setLocalQty: (value: string) => void;
  debounceRef: React.RefObject<ReturnType<typeof setTimeout> | undefined>;
  onPendingChange?: (pending: boolean) => void;
  commit: (value: number) => Promise<void>;
}) {
  const { base, minQuantity, step, setLocalQty, debounceRef, commit } = input;
  const baseRef = useRef(base);
  const held = useRef<number | null>(null);
  const heldFrom = useRef(base);
  useEffect(() => {
    baseRef.current = base;
  });

  const tick = (direction: 1 | -1) => {
    if (held.current === null) heldFrom.current = baseRef.current;
    const current = held.current ?? baseRef.current;
    const next =
      direction > 0
        ? stepQuantityUp(current, step)
        : stepQuantityDown(current, step, minQuantity);
    if (next === current) return false;
    held.current = next;
    clearTimeout(debounceRef.current);
    input.onPendingChange?.(true);
    setLocalQty(formatNumber(next));
    return true;
  };
  const release = () => {
    const value = held.current;
    held.current = null;
    if (value !== null) void commit(value);
  };
  const cancel = () => {
    if (held.current === null) return;
    held.current = null;
    input.onPendingChange?.(false);
    setLocalQty(formatNumber(heldFrom.current));
  };

  const ends = { onRelease: release, onCancel: cancel };
  return {
    decrease: useHoldRepeat({ onTick: () => tick(-1), ...ends }),
    increase: useHoldRepeat({ onTick: () => tick(1), ...ends }),
  };
}

function typedQuantityHandlers(input: {
  quantity: number;
  minQuantity: number;
  localQty: string;
  setLocalQty: (value: string) => void;
  debounceRef: React.RefObject<ReturnType<typeof setTimeout> | undefined>;
  onPendingChange?: (pending: boolean) => void;
  cancelPending: () => void;
  commit: (value: number) => Promise<void>;
}) {
  const { minQuantity, debounceRef, onPendingChange, commit } = input;
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value.replace(/[^0-9]/g, "");
    input.setLocalQty(raw);
    const value = parseInt(raw, 10);
    clearTimeout(debounceRef.current);
    onPendingChange?.(true);
    if (isCheckoutQuantityAllowed(value, minQuantity)) {
      debounceRef.current = setTimeout(() => {
        void commit(value);
      }, 400);
    }
  };
  const handleBlur = () => {
    const value = parseInt(input.localQty.replace(/[^0-9]/g, ""), 10);
    if (isCheckoutQuantityAllowed(value, minQuantity)) {
      void commit(value);
    } else {
      input.cancelPending();
      input.setLocalQty(formatNumber(input.quantity));
    }
  };
  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    event.currentTarget.select();
  };
  return { handleChange, handleBlur, handleFocus };
}

export function useQuantityStepper(input: QuantityStepperInput) {
  const { quantity, minQuantity, step = 1, onPendingChange } = input;
  const commitState = useQuantityCommit(input);
  const { localQty, setLocalQty, debounceRef, commit } = commitState;

  const typed = parseInt(localQty.replace(/[^0-9]/g, ""), 10);
  const base = isCheckoutQuantityAllowed(typed, minQuantity)
    ? typed
    : quantity;
  const held = useHeldSteps({
    base,
    minQuantity,
    step,
    setLocalQty,
    debounceRef,
    onPendingChange,
    commit,
  });

  return {
    localQty,
    base,
    decrease: held.decrease,
    increase: held.increase,
    ...typedQuantityHandlers({
      ...commitState,
      quantity,
      minQuantity,
      onPendingChange,
    }),
  };
}
